import { type KeyboardEvent, type ReactNode, useCallback, useRef, useState } from "react";

import {
  Input as ReactAriaInput,
  type InputProps as ReactAriaInputProps,
  TextField as ReactAriaTextField,
  type TextFieldProps as ReactAriaTextFieldProps,
} from "react-aria-components";

import { FormFieldAssistiveText } from "#components/shared/FormFieldAssistiveText";
import { FormFieldHeader } from "#components/shared/FormFieldHeader";
import { ClearIcon } from "#icons";
import { cn, customTv } from "#utils";

const tagStyles = customTv({
  base: "inline-flex items-center justify-center gap-x-xs px-xs text-text-primary rounded-md bg-data-viz-soft-indigo text-label-extra-small-strong",
  variants: {
    isDisabled: {
      true: "opacity-50",
    },
    isReadOnly: {
      true: "!text-label-small",
    },
  },
});

const removeButtonStyles = customTv({
  base: "inline-flex cursor-pointer items-center hover:text-outline-input-hover focus-visible:text-outline-input-focus focus-visible:outline-none",
});

const inputWrapperStyles = customTv({
  base: `
    text-label-medium text-text-primary outline-none border border-outline-input-enabled bg-primary rounded-1 leading-md-20
    hover:border-outline-input-hover hover:ring-1 hover:ring-outline-input-hover
    flex flex-wrap items-center gap-xs py-xs px-lg
  `,
  variants: {
    isReadOnly: {
      true: "!cursor-text !bg-background-secondary !text-text-primary !border-transparent hover:ring-0",
    },
    isInvalid: {
      true: "border-outline-negative ring-1 ring-outline-negative",
    },
    isInputFocused: {
      true: "ring-1 !ring-outline-input-focus !border-outline-input-focus",
    },
    isDisabled: {
      true: "cursor-not-allowed bg-background-primary text-text-disabled border-outline-input-disabled hover:ring-0 hover:border-outline-input-disabled",
    },
  },
});

/** Represents a single tag in the MultiTextField */
export interface MultiTextFieldItem {
  /** Unique identifier for the tag */
  id: string;
  /** Display name for the tag */
  name: string;
}

interface InputProps extends ReactAriaInputProps {
  className?: string;
}

/** MultiTextFieldProps interface for the MultiTextField component */
export interface MultiTextFieldProps extends Omit<
  ReactAriaTextFieldProps,
  "value" | "defaultValue" | "onChange"
> {
  /** Array of selected tags (controlled mode) */
  value?: MultiTextFieldItem[];
  /** Default tags (uncontrolled mode) */
  defaultValue?: MultiTextFieldItem[];
  /** Callback when tags change (works in both controlled and uncontrolled modes) */
  onChange?: (items: MultiTextFieldItem[]) => void;
  inputProps?: InputProps;
  label?: string;
  assistiveText?: ReactNode;
  tooltipText?: ReactNode;
  className?: string;
}

/**
 * MultiTextField component - A text input that lets users type a value and press
 * Enter to add it as a removable tag. Any unapplied value is also added when the
 * input loses focus. Press Backspace on an empty input to remove the last tag.
 *
 * Supports both controlled (value + onChange) and uncontrolled (defaultValue + onChange) modes.
 */
export function MultiTextField({
  value: controlledValue,
  defaultValue,
  onChange,
  inputProps,
  label,
  tooltipText,
  className,
  assistiveText,
  isDisabled,
  isReadOnly,
  isInvalid,
  ...props
}: MultiTextFieldProps) {
  const isControlled = controlledValue !== undefined;

  const [internalItems, setInternalItems] = useState<MultiTextFieldItem[]>(defaultValue ?? []);
  const [inputValue, setInputValue] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const items = isControlled ? controlledValue : internalItems;

  const canEdit = !isDisabled && !isReadOnly;

  const updateItems = useCallback(
    (updatedItems: MultiTextFieldItem[]) => {
      if (!isControlled) {
        setInternalItems(updatedItems);
      }
      onChange?.(updatedItems);
    },
    [isControlled, onChange],
  );

  const addTag = useCallback(
    (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;

      const newItem: MultiTextFieldItem = {
        id: crypto.randomUUID(),
        name: trimmed,
      };

      updateItems([...items, newItem]);
      setInputValue("");
    },
    [items, updateItems],
  );

  const removeItem = useCallback(
    (id: string) => {
      updateItems(items.filter((item) => item.id !== id));
    },
    [items, updateItems],
  );

  const removeLastItem = useCallback(() => {
    if (items.length === 0) return;
    updateItems(items.slice(0, -1));
  }, [items, updateItems]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!canEdit) return;

      if (e.key === "Enter") {
        e.preventDefault();
        addTag(inputValue);
      } else if (e.key === "Backspace" && inputValue === "") {
        e.preventDefault();
        removeLastItem();
      }

      inputProps?.onKeyDown?.(e);
    },
    [canEdit, inputValue, addTag, removeLastItem, inputProps],
  );

  return (
    <ReactAriaTextField
      aria-label={label ?? "multi-text-field"}
      className={cn("flex flex-col gap-[.15rem]", className)}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      onChange={canEdit ? setInputValue : undefined}
      value={inputValue}
      {...props}
    >
      <FormFieldHeader isDisabled={isDisabled} label={label} tooltipText={tooltipText} />
      <div
        className={inputWrapperStyles({
          isInvalid,
          isInputFocused,
          isDisabled,
          isReadOnly,
          className: inputProps?.className,
        })}
        onClick={() => {
          inputRef.current?.focus();
        }}
      >
        {items.length > 0 ? (
          <span className="flex flex-wrap items-center gap-xs" role="list">
            {items.map((item) => (
              <span
                className={tagStyles({ isDisabled, isReadOnly })}
                key={item.id}
                onClick={(e) => e.stopPropagation()}
                role="listitem"
              >
                {item.name}
                {canEdit ? (
                  <span
                    aria-label={`Remove ${item.name}`}
                    className={removeButtonStyles()}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeItem(item.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                        e.preventDefault();
                        removeItem(item.id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <ClearIcon size={14} />
                  </span>
                ) : null}
              </span>
            ))}
          </span>
        ) : null}
        <ReactAriaInput
          {...inputProps}
          className={cn(
            "min-w-[4rem] flex-1 border-0 bg-transparent outline-0",
            "disabled:cursor-not-allowed disabled:text-text-disabled",
            "placeholder:text-text-secondary focus-visible:outline-none disabled:placeholder:text-text-disabled",
          )}
          onBlur={(e) => {
            if (isReadOnly) return;
            setIsInputFocused(false);
            if (canEdit) {
              addTag(inputValue);
            }
            inputProps?.onBlur?.(e);
          }}
          onFocus={(e) => {
            if (isReadOnly) return;
            setIsInputFocused(true);
            inputProps?.onFocus?.(e);
          }}
          onKeyDown={handleKeyDown}
          placeholder={inputProps?.placeholder ?? "Type and press Enter..."}
          ref={inputRef}
        />
      </div>
      <FormFieldAssistiveText isDisabled={isDisabled} isInvalid={isInvalid}>
        {assistiveText}
      </FormFieldAssistiveText>
    </ReactAriaTextField>
  );
}
